/**
 * Workspace design tokens and shared styling.
 *
 * Styling is a single hand-written stylesheet rather than a component library:
 * the workspace needs dense operational tables and a fixed shell, and shipping an
 * entire themed component system for that would add far more surface than it
 * removes. Rules are written mobile-first with fluid grid tracks so the dense
 * layout still collapses cleanly on a narrow screen.
 *
 * Colour choices carry meaning and are never the only signal: severity also
 * renders as a text label, so colour-blind users and printed output stay readable.
 */

export const styles = `
:root {
  color-scheme: light;

  /* Surface ladder - tonal progression from pure white, subtle cool tints for tech/SaaS context */
  --surface-00: #FFFFFF;
  --surface-01: #F8FAFB;
  --surface-02: #F1F5F9;
  --surface-03: #E2E8F0;
  --surface-04: #CBD5E1;
  --surface-05: #94A3B8;
  --surface-06: #64748B;

  /* Legacy surface mappings for gradual migration */
  --bg: var(--surface-01);
  --surface: var(--surface-00);
  --surface-alt: var(--surface-02);
  
  /* Borders - subtle and refined for clean aesthetic */
  --border: rgba(15, 23, 42, 0.08);
  --border-strong: rgba(15, 23, 42, 0.12);
  --border-glow: rgba(59, 130, 246, 0.25);

  /* Text hierarchy - clear contrast for readability */
  --text: #0F172A;
  --text-muted: #475569;
  --text-faint: #94A3B8;

  /* Accent - vibrant blue for SaaS tech feel */
  --accent: #3B82F6;
  --accent-hover: #2563EB;
  --accent-soft: rgba(59, 130, 246, 0.08);
  --accent-glow: rgba(59, 130, 246, 0.2);

  /* Status colors - clear and purposeful */
  --ok: #10B981;
  --ok-soft: rgba(16, 185, 129, 0.1);
  --warn: #F59E0B;
  --warn-soft: rgba(245, 158, 11, 0.1);
  --danger: #EF4444;
  --danger-soft: rgba(239, 68, 68, 0.1);
  --neutral-soft: var(--surface-03);

  /* Spacing tokens - generous for breathing room */
  --space-xs: 0.25rem;
  --space-sm: 0.5rem;
  --space-md: 1rem;
  --space-lg: 1.5rem;
  --space-xl: 2rem;
  --space-2xl: 3rem;

  /* Radius tokens - fully rounded for modern SaaS */
  --radius: 12px;
  --radius-sm: 6px;
  --radius-lg: 16px;
  --radius-xl: 24px;
  --radius-full: 999px;

  /* Layout dimensions */
  --shell-header: 64px;
  --sidebar: 240px;

  /* Typography - clean, modern, tech-focused */
  --font-display: 'Space Grotesk', -apple-system, BlinkMacSystemFont, sans-serif;
  --font-body: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  --font: var(--font-body);
  --mono: 'JetBrains Mono', 'SF Mono', Consolas, monospace;

  /* Shadows for depth */
  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.08);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  --shadow-glow: 0 0 20px rgba(59, 130, 246, 0.15);

  /* Animation timing functions */
  --ease-smooth: cubic-bezier(0.4, 0, 0.2, 1);
  --ease-bounce: cubic-bezier(0.68, -0.55, 0.265, 1.55);
  --ease-spring: cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  /* Animation durations */
  --duration-fast: 150ms;
  --duration-normal: 250ms;
  --duration-slow: 350ms;
}

* { 
  box-sizing: border-box; 
}

html, body, #root { 
  height: 100%; 
}

body {
  margin: 0;
  background: var(--bg);
  color: var(--text);
  font-family: var(--font);
  font-size: clamp(13px, 1vw, 14px);
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* ============================================================ Animations === */

/* Fade in animation for page content */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Slide up animation */
@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Slide in from left */
@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Scale in animation */
@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Number count animation */
@keyframes countUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Pulse animation */
@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
}

/* Shimmer animation for loading states */
@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Glow animation */
@keyframes glow {
  0%, 100% {
    box-shadow: 0 0 5px rgba(59, 130, 246, 0.3);
  }
  50% {
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.6);
  }
}

/* Progress bar animation */
@keyframes progress {
  from {
    width: 0%;
  }
}

/* Stagger animation delays */
.animate-stagger-1 { animation-delay: 50ms; }
.animate-stagger-2 { animation-delay: 100ms; }
.animate-stagger-3 { animation-delay: 150ms; }
.animate-stagger-4 { animation-delay: 200ms; }
.animate-stagger-5 { animation-delay: 250ms; }

/* Reduce motion for accessibility */
@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

h1, h2, h3, h4 { 
  margin: 0; 
  font-weight: 600; 
  line-height: 1.2; 
  font-family: var(--font-display); 
  letter-spacing: -0.025em;
}
h1 { font-size: clamp(20px, 2.5vw, 24px); }
h2 { font-size: clamp(16px, 2vw, 18px); }
h3 { font-size: clamp(14px, 1.5vw, 16px); }
p { margin: 0; line-height: 1.7; }

a { 
  color: var(--accent); 
  text-decoration: none; 
  transition: color 0.2s ease;
}
a:hover { 
  color: var(--accent-hover); 
}

/* Minimal focus for clean aesthetic */
:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
  border-radius: var(--radius-sm);
}

/* Selection styling */
::selection {
  background-color: var(--accent);
  color: var(--surface-00);
}

/* Minimal scrollbar */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: var(--surface-04);
  border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
  background: var(--surface-05);
}

/* ----------------------------------------------------------------- shell -- */

.shell {
  display: grid;
  grid-template-columns: var(--sidebar) 1fr;
  grid-template-rows: var(--shell-header) 1fr;
  grid-template-areas: "brand header" "nav main";
  height: 100%;
  background: var(--surface-01);
}

.shell__brand {
  grid-area: brand;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 24px;
  background: var(--surface-00);
  color: var(--text);
  border-bottom: 1px solid var(--border);
  border-right: 1px solid var(--border);
}
.shell__brand-mark {
  width: 32px; 
  height: 32px; 
  flex: none;
  border-radius: var(--radius-sm);
  background: linear-gradient(135deg, var(--accent) 0%, var(--accent-hover) 100%);
  display: grid; 
  place-items: center;
  font-size: 12px; 
  font-weight: 800;
  color: #fff;
  font-family: var(--mono);
  box-shadow: 0 0 0 4px var(--accent-soft);
}
.shell__brand-text { 
  font-size: 15px; 
  font-weight: 700; 
  letter-spacing: -0.015em;
  font-family: var(--font-display);
}

.shell__header {
  grid-area: header;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 24px;
  background: var(--surface-00);
  border-bottom: 1px solid var(--border);
  min-width: 0;
}
.shell__header-spacer { flex: 1 1 auto; min-width: 0; }

.shell__nav {
  grid-area: nav;
  background: var(--surface-00);
  padding: 16px 0 24px;
  overflow-y: auto;
  border-right: 1px solid var(--border);
}
.shell__nav-group { 
  padding: 16px 20px 8px; 
  font-size: 11px; 
  color: var(--text-faint); 
  letter-spacing: 0.08em; 
  text-transform: uppercase;
  font-weight: 700;
  font-family: var(--font-display);
}
.shell__nav-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  margin: 0 12px 4px;
  color: var(--text-muted);
  font-size: 14px;
  border-radius: var(--radius-sm);
  transition: all 0.2s ease;
  font-weight: 500;
  border-left: none;
}
.shell__nav-link:hover { 
  background: var(--surface-02); 
  color: var(--text); 
  text-decoration: none;
}
.shell__nav-link--active {
  background: var(--accent-soft);
  color: var(--accent);
  font-weight: 600;
  border-left: none;
}
.shell__nav-count {
  margin-left: auto;
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: var(--text-faint);
  background: var(--surface-04);
  padding: 3px 8px;
  border-radius: var(--radius-full);
  font-family: var(--mono);
  font-weight: 600;
}

.shell__main {
  grid-area: main;
  overflow: auto;
  padding: 24px 32px 40px;
  min-width: 0;
}
.page-transition {
  animation: slideUp var(--duration-slow) var(--ease-smooth) both;
}

.shell__mobile-nav { display: none; }

/* ------------------------------------------------------------------ bar --- */

.page-head {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.page-head__titles { min-width: 0; }
.page-head__desc { 
  color: var(--text-muted); 
  font-size: 14px; 
  margin-top: 4px; 
  line-height: 1.6;
}
.page-head__actions { 
  margin-left: auto; 
  display: flex; 
  flex-wrap: wrap;
  gap: 12px; 
  align-items: center; 
}

/* Clean stat cards with subtle borders and animations */
.statbar {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}
.statbar__cell { 
  background: var(--surface-00); 
  padding: 20px 24px;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  transition: all var(--duration-normal) var(--ease-smooth);
  box-shadow: var(--shadow-sm);
  animation: scaleIn var(--duration-slow) var(--ease-spring) both;
  position: relative;
  overflow: hidden;
}
.statbar__cell::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--accent), var(--accent-hover));
  transform: scaleX(0);
  transform-origin: left;
  transition: transform var(--duration-normal) var(--ease-smooth);
}
.statbar__cell:hover {
  border-color: var(--accent);
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}
.statbar__cell:hover::before {
  transform: scaleX(1);
}
.statbar__label {
  font-size: 12px;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
  margin-bottom: 8px;
}
.statbar__value {
  font-size: 28px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--text);
  font-family: var(--font-display);
  line-height: 1;
}
.statbar__figure {
  display: inline-block;
  animation: countUp var(--duration-slow) var(--ease-smooth) both;
}
.statbar__hint { 
  font-size: 12px; 
  color: var(--text-faint); 
  margin-top: 6px;
}

/* ---------------------------------------------------------------- panel --- */

.panel {
  background: var(--surface-00);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  margin-bottom: 24px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  transition: box-shadow var(--duration-normal) var(--ease-smooth);
}
.panel:hover {
  box-shadow: var(--shadow-md);
}
.panel__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid var(--border);
  background: var(--surface-01);
  flex-wrap: wrap;
  transition: background var(--duration-fast) var(--ease-smooth);
}
.panel__title { 
  font-size: 16px; 
  font-weight: 600;
  font-family: var(--font-display);
  letter-spacing: -0.015em;
}
.panel__sub { 
  font-size: 12px; 
  color: var(--text-muted); 
}
.panel__actions { 
  margin-left: auto; 
  display: flex; 
  gap: 8px; 
  align-items: center; 
}
.panel__body { 
  padding: 24px;
  animation: fadeIn var(--duration-slow) var(--ease-smooth);
}
.panel__body--flush { padding: 0; }

.split {
  display: grid;
  gap: 24px;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  align-items: start;
}
@media (max-width: 1080px) {
  .split { grid-template-columns: minmax(0, 1fr); }
}

/* ---------------------------------------------------------------- table --- */

.table-wrap { 
  width: 100%; 
  overflow-x: auto;
  margin: -1px;
}
table.data {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
table.data th {
  text-align: left;
  font-weight: 600;
  font-size: 11px;
  color: var(--text-faint);
  background: var(--surface-02);
  padding: 12px 24px;
  border-bottom: 1px solid var(--border);
  white-space: nowrap;
  position: sticky;
  top: 0;
  z-index: 1;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
table.data td {
  padding: 16px 24px;
  border-bottom: 1px solid var(--border);
  vertical-align: middle;
}
table.data tbody tr:last-child td { border-bottom: none; }
table.data tbody tr {
  transition: all var(--duration-fast) var(--ease-smooth);
  animation: slideInLeft var(--duration-normal) var(--ease-smooth);
  animation-fill-mode: both;
}
table.data tbody tr:nth-child(1) { animation-delay: 0ms; }
table.data tbody tr:nth-child(2) { animation-delay: 50ms; }
table.data tbody tr:nth-child(3) { animation-delay: 100ms; }
table.data tbody tr:nth-child(4) { animation-delay: 150ms; }
table.data tbody tr:nth-child(5) { animation-delay: 200ms; }
table.data tbody tr:nth-child(6) { animation-delay: 250ms; }
table.data tbody tr:hover { 
  background: var(--surface-02);
  box-shadow: inset 3px 0 0 var(--accent);
}
.num { 
  font-variant-numeric: tabular-nums; 
  font-family: var(--mono);
  font-weight: 500;
}
.nowrap { white-space: nowrap; }
.col-tight { width: 1%; white-space: nowrap; }
.cell-truncate {
  max-width: 400px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cell-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 480px;
  line-height: 1.5;
}

/* ----------------------------------------------------------------- misc --- */

.badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: var(--radius-full);
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  border: none;
  font-family: var(--mono);
  letter-spacing: 0.02em;
  text-transform: uppercase;
  transition: all var(--duration-fast) var(--ease-smooth);
  animation: scaleIn var(--duration-fast) var(--ease-smooth);
}
.badge:hover {
  transform: scale(1.05);
}
.badge--ok { 
  background: var(--ok-soft); 
  color: var(--ok);
}
.badge--warn { 
  background: var(--warn-soft); 
  color: var(--warn);
}
.badge--danger { 
  background: var(--danger-soft); 
  color: var(--danger);
}
.badge--info { 
  background: var(--accent-soft); 
  color: var(--accent);
}
.badge--neutral { 
  background: var(--neutral-soft); 
  color: var(--text-muted);
}

.muted { color: var(--text-muted); }
.faint { color: var(--text-faint); }
.sep { 
  height: 1px; 
  background: var(--border); 
  margin: 24px 0; 
}

.stack { 
  display: flex; 
  flex-direction: column; 
  gap: 16px; 
}
.row { 
  display: flex; 
  align-items: center; 
  gap: 12px; 
  flex-wrap: wrap; 
}
.row--tight { gap: 8px; }

/* ---------------------------------------------------------------- forms --- */

.field { 
  display: flex; 
  flex-direction: column; 
  gap: 6px; 
}
.field__label { 
  font-size: 13px; 
  font-weight: 600; 
  color: var(--text);
}
.field__hint { 
  font-size: 12px; 
  color: var(--text-muted); 
}
.field__error { 
  font-size: 12px; 
  color: var(--danger); 
  font-weight: 500; 
}

input[type="text"], 
input[type="email"], 
input[type="password"], 
input[type="search"], 
input[type="number"],
input[type="url"],
input[type="tel"],
input[type="date"],
input[type="time"],
input[type="datetime-local"],
textarea, 
select {
  font: inherit;
  font-size: 14px;
  color: var(--text);
  background: var(--surface-03);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  padding: 10px 14px;
  width: 100%;
  min-width: 0;
  transition: all 0.2s ease;
}
input:focus, 
textarea:focus, 
select:focus {
  background: var(--surface-04);
  border-color: var(--accent);
  outline: none;
  box-shadow: 0 0 0 3px var(--accent-soft);
}
input:disabled, 
textarea:disabled, 
select:disabled { 
  background: var(--surface-02); 
  color: var(--text-faint); 
  opacity: 0.6;
  cursor: not-allowed;
}
textarea { 
  resize: vertical; 
  min-height: 100px; 
  font-family: var(--mono); 
  font-size: 13px; 
  line-height: 1.6;
}
select { 
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5l5 5 5-5' stroke='%23A1A8B3' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 36px;
}

.btn {
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border-strong);
  background: var(--surface-03);
  color: var(--text);
  cursor: pointer;
  white-space: nowrap;
  transition: all var(--duration-normal) var(--ease-smooth);
  letter-spacing: -0.01em;
}
.btn:hover:not(:disabled) { 
  background: var(--surface-04); 
  border-color: var(--accent);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}
.btn:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: var(--shadow-sm);
}
.btn:disabled { 
  opacity: 0.4; 
  cursor: not-allowed;
  transform: none;
}
.btn--primary { 
  background: var(--accent); 
  border-color: var(--accent); 
  color: #fff; 
  font-weight: 700;
}
.btn--primary:hover:not(:disabled) { 
  background: var(--accent-hover); 
  border-color: var(--accent-hover);
  box-shadow: var(--shadow-glow);
}
.btn--danger { 
  color: var(--danger); 
  border-color: var(--danger);
  background: var(--danger-soft);
}
.btn--danger:hover:not(:disabled) { 
  background: var(--danger);
  color: #fff;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}
.btn--sm { 
  padding: 6px 14px; 
  font-size: 13px; 
}

/* --------------------------------------------------------------- states --- */

.notice {
  display: flex;
  gap: 14px;
  padding: 16px 20px;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: var(--surface-02);
  font-size: 13px;
  animation: slideUp var(--duration-normal) var(--ease-smooth);
  transition: all var(--duration-normal) var(--ease-smooth);
}
.notice:hover {
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}
.notice__icon { 
  flex: none; 
  font-weight: 700; 
  font-size: 16px;
  line-height: 1;
  animation: scaleIn var(--duration-normal) var(--ease-bounce);
}
.notice__body { min-width: 0; }
.notice__title { 
  font-weight: 600; 
  color: var(--text);
  margin-bottom: 4px;
}
.notice__detail { 
  color: var(--text-muted); 
  line-height: 1.6; 
}
.notice--error { 
  background: var(--danger-soft); 
  border-color: rgba(239, 68, 68, 0.3);
}
.notice--error .notice__icon { color: var(--danger); }
.notice--warn { 
  background: var(--warn-soft); 
  border-color: rgba(245, 158, 11, 0.3);
}
.notice--warn .notice__icon { color: var(--warn); }
.notice--info { 
  background: var(--accent-soft); 
  border-color: var(--border-glow);
}
.notice--info .notice__icon { color: var(--accent); }

.state {
  padding: 40px 24px;
  text-align: center;
  color: var(--text-muted);
  font-size: 14px;
  animation: fadeIn var(--duration-slow) var(--ease-smooth);
}
.state__title { 
  color: var(--text); 
  font-weight: 600; 
  font-size: 16px; 
  margin-bottom: 8px;
  font-family: var(--font-display);
}
.state__actions { 
  margin-top: 16px; 
  display: flex; 
  gap: 12px; 
  justify-content: center; 
}

.skeleton-row { 
  display: flex; 
  flex-direction: column; 
  gap: 12px; 
  padding: 20px; 
}
.skeleton-bar {
  height: 14px;
  border-radius: var(--radius-sm);
  background: linear-gradient(
    90deg, 
    var(--surface-03) 25%, 
    var(--surface-04) 50%, 
    var(--surface-03) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .skeleton-bar { animation: none; }
}

/* ----------------------------------------------------------------- auth --- */

.auth {
  min-height: 100%;
  display: grid;
  place-items: center;
  padding: 32px;
  background: var(--bg);
}
.auth__box {
  width: 100%;
  max-width: 420px;
  background: var(--surface-02);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 40px;
}
.auth__brand { 
  display: flex; 
  align-items: center; 
  gap: 12px; 
  margin-bottom: 8px; 
}
.auth__title { 
  font-size: 24px; 
  font-weight: 600;
  font-family: var(--font-display);
  letter-spacing: -0.02em;
}
.auth__sub { 
  color: var(--text-muted); 
  font-size: 14px; 
  margin-bottom: 24px; 
  line-height: 1.6;
}
.auth__form { 
  display: flex; 
  flex-direction: column; 
  gap: 16px; 
}
.auth__note { margin-top: 20px; }

/* ----------------------------------------------------------------- misc --- */

.mono { 
  font-family: var(--mono); 
  font-size: 12px;
  font-weight: 500;
}
.truncate-id {
  font-family: var(--mono);
  font-size: 12px;
  color: var(--text-muted);
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  vertical-align: bottom;
}
.tenant-picker { 
  display: flex; 
  align-items: center; 
  gap: 10px; 
  min-width: 0; 
}
.tenant-picker select { 
  min-width: 200px; 
  width: auto; 
  max-width: 280px; 
}
.user-chip {
  display: flex; 
  align-items: center; 
  gap: 12px;
  padding-left: 16px; 
  border-left: 1px solid var(--border);
  min-width: 0;
}
.user-chip__avatar {
  width: 32px; 
  height: 32px; 
  flex: none;
  border-radius: var(--radius-full);
  background: var(--accent-soft);
  color: var(--accent);
  display: grid; 
  place-items: center;
  font-size: 12px; 
  font-weight: 700;
  font-family: var(--mono);
  border: 2px solid var(--accent);
}
.user-chip__text { 
  min-width: 0; 
  line-height: 1.4; 
}
.user-chip__email {
  font-size: 12px; 
  color: var(--text-muted);
  max-width: 200px; 
  overflow: hidden; 
  text-overflow: ellipsis; 
  white-space: nowrap;
}

/* ----------------------------------------------------------- responsive --- */

@media (max-width: 860px) {
  .shell {
    grid-template-columns: 1fr;
    grid-template-rows: var(--shell-header) auto 1fr;
    grid-template-areas: "header" "nav" "main";
  }
  .shell__brand { display: none; }
  /* Narrow screens: the grouped nav becomes a drawer opened from the header. */
  .shell__nav-toggle { display: inline-flex; }
  /* Platform console keeps the horizontal strip. */
  .shell__nav:not(.shell__nav--drawer) {
    display: flex;
    gap: 0;
    padding: 0 8px;
    overflow-x: auto;
    border-bottom: 1px solid var(--border);
    border-right: none;
  }
  .shell__nav:not(.shell__nav--drawer) .shell__nav-link {
    border-radius: 0;
    border-bottom: 3px solid transparent;
    padding: 12px 16px;
    white-space: nowrap;
    margin: 0;
  }
  .shell__nav:not(.shell__nav--drawer) .shell__nav-link--active {
    border-bottom-color: var(--accent);
    background: transparent;
  }
  .shell__nav--drawer {
    display: none;
    border-right: none;
    border-bottom: 1px solid var(--border);
    padding: 8px 0 12px;
    max-height: calc(100vh - var(--shell-header));
  }
  .shell__nav--drawer.shell__nav--open {
    display: block;
    animation: fadeIn var(--duration-fast) var(--ease-smooth);
  }
  .shell__header { padding: 0 16px; gap: 12px; }
  .shell__main { padding: 16px 16px 32px; }
  .user-chip__email { display: none; }
  .statbar {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 560px) {
  .tenant-picker select { min-width: 0; max-width: 180px; }
  .page-head__actions { margin-left: 0; width: 100%; }
  .cell-truncate, .cell-clamp { max-width: 260px; }
}

/* ============================================================ Analysis === */

/* Color tokens for analysis module */
:root {
  --color-bg: var(--bg);
  --color-bg-subtle: var(--surface-02);
  --color-text: var(--text);
  --color-text-muted: var(--text-muted);
  --color-border: var(--border);
  --color-primary: var(--accent);
  --color-success: var(--ok);
  --color-warning: var(--warn);
  --color-danger: var(--danger);
  --color-neutral: var(--text-faint);
}

/* Stat Grid */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  padding: 1.5rem;
  text-align: center;
}

.stat-card__label {
  font-size: 0.875rem;
  color: var(--text-muted);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-card__value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text);
}

.stat-card__value--sm {
  font-size: 1.25rem;
}

.stat-card__value--danger {
  color: var(--danger);
}

/* Distribution Chart */
.distribution-chart {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
}

.distribution-item {
  display: grid;
  grid-template-columns: 150px 1fr 60px;
  gap: 1rem;
  align-items: center;
}

.distribution-item__label {
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
}

.distribution-item__bar {
  height: 24px;
  background-color: var(--surface-02);
  border-radius: 4px;
  overflow: hidden;
}

.distribution-item__fill {
  height: 100%;
  transition: width 0.3s ease;
}

.distribution-item__value {
  text-align: right;
  font-weight: 600;
  font-size: 0.875rem;
}

/* Grid Layout */
.grid {
  display: grid;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.grid--2col {
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
}

/* Table Enhancements */
.table-container {
  overflow-x: auto;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th {
  background-color: var(--surface-02);
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.875rem;
  border-bottom: 2px solid var(--border);
}

.table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border);
}

.table tbody tr:hover {
  background-color: var(--surface-02);
}

.text-center {
  text-align: center;
}

.text-danger {
  color: var(--danger);
}

.text-success {
  color: var(--ok);
}

.text-warning {
  color: var(--warn);
}

/* Time Range Selector */
.time-range-selector {
  display: flex;
  gap: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0.25rem;
  background-color: var(--surface-00);
}

.time-range-selector__option {
  padding: 0.5rem 1rem;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 4px;
  font-size: 0.875rem;
  transition: all 0.2s;
}

.time-range-selector__option:hover {
  background-color: var(--surface-02);
}

.time-range-selector__option--active {
  background-color: var(--accent);
  color: white;
  font-weight: 600;
}

/* Input styles */
.input {
  font: inherit;
  font-size: 14px;
  color: var(--text);
  background: var(--surface-00);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  padding: 10px 14px;
  transition: all 0.2s ease;
}

.input--sm {
  padding: 6px 10px;
  font-size: 13px;
}

.input[type="date"] {
  cursor: pointer;
}

.select {
  font: inherit;
  font-size: 14px;
  color: var(--text);
  background: var(--surface-00);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  padding: 10px 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.select--sm {
  padding: 6px 10px;
  font-size: 13px;
}

/* Responsive */
@media (max-width: 768px) {
  .stat-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .grid--2col {
    grid-template-columns: 1fr;
  }

  .distribution-item {
    grid-template-columns: 100px 1fr 50px;
    gap: 0.5rem;
  }
}

/* === Propagation Tree Styles === */

.stat-item {
  margin-bottom: 1rem;
}

.stat-item__label {
  font-size: 0.75rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
}

.stat-item__value {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text);
}

/* Propagation tree node styles */
.propagation-tree-node {
  position: relative;
  margin-left: 2rem;
  padding-left: 1rem;
  border-left: 2px solid var(--border);
}

.propagation-tree-node::before {
  content: '';
  position: absolute;
  left: -2px;
  top: 50%;
  width: 1rem;
  height: 2px;
  background-color: var(--border);
}

.propagation-tree-node--root {
  margin-left: 0;
  padding-left: 0;
  border-left: none;
}

.propagation-tree-node--root::before {
  display: none;
}

.propagation-node-card {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: var(--surface-00);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  transition: all 0.2s;
  cursor: pointer;
}

.propagation-node-card:hover {
  background: var(--surface-01);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.propagation-node-card--expanded {
  background: var(--surface-01);
  border-color: var(--accent);
}

/* === Timeline Styles === */

.timeline {
  position: relative;
  padding: 1rem 0;
}

.timeline-event {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  position: relative;
}

.timeline-event__marker {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.timeline-event__dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: var(--accent);
  border: 2px solid var(--surface-00);
  z-index: 2;
  flex-shrink: 0;
}

.timeline-event__line {
  width: 2px;
  flex: 1;
  background-color: var(--border);
  margin-top: 4px;
  min-height: 40px;
}

.timeline-event__content {
  flex: 1;
  background: var(--surface-00);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.timeline-event__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.timeline-event__stats {
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
}

.timeline-stat {
  display: flex;
  gap: 0.25rem;
  font-size: 0.875rem;
}

.timeline-stat__label {
  color: var(--text-muted);
}

.timeline-stat__value {
  font-weight: 600;
  color: var(--text);
}

/* ============================================================== Charts === */

.chart-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 24px;
}
.chart-grid > .panel { margin-bottom: 0; }
.chart { display: flex; flex-direction: column; gap: 14px; }
.chart--row { flex-direction: row; align-items: center; gap: 24px; flex-wrap: wrap; }

.chart-legend {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  font-size: 12.5px;
}
.chart--row .chart-legend { flex-direction: column; flex: 1 1 120px; }
.chart-legend__item { display: flex; align-items: center; gap: 8px; }
.chart-legend__item .num { margin-left: auto; padding-left: 8px; }
.chart-legend__dot { width: 10px; height: 10px; border-radius: 3px; flex: none; }

.stacked-bar {
  display: flex;
  height: 14px;
  border-radius: var(--radius-full);
  overflow: hidden;
  background: var(--surface-02);
  gap: 2px;
}
.stacked-bar__seg {
  height: 100%;
  transform-origin: left;
  animation: growX var(--duration-slow) var(--ease-smooth) both;
  transition: width var(--duration-slow) var(--ease-smooth), filter var(--duration-fast);
}
.stacked-bar__seg:hover { filter: brightness(1.1); }
.stacked-bar__empty { flex: 1; }
@keyframes growX { from { transform: scaleX(0); } to { transform: scaleX(1); } }

.donut { width: 132px; height: 132px; flex: none; }
.donut__track { fill: none; stroke: var(--surface-02); stroke-width: 14; }
.donut__arc {
  fill: none;
  stroke-width: 14;
  transform: rotate(-90deg);
  transform-origin: 60px 60px;
  animation: drawArc 700ms var(--ease-smooth) both;
  transition: stroke-width var(--duration-fast);
}
.donut__arc:hover { stroke-width: 17; }
@keyframes drawArc { from { stroke-dasharray: 0 999; } }
.donut__total {
  text-anchor: middle;
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  fill: var(--text);
}
.donut__caption { text-anchor: middle; font-size: 10px; fill: var(--text-faint); }

.chart-empty { padding: 16px 0; text-align: center; font-size: 13px; }

.rank-bars { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.rank-bars__row {
  display: grid;
  grid-template-columns: minmax(64px, 30%) 1fr auto;
  align-items: center;
  gap: 12px;
  font-size: 12.5px;
}
.rank-bars__label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--text-muted); }
.rank-bars__track { height: 10px; background: var(--surface-02); border-radius: var(--radius-full); overflow: hidden; }
.rank-bars__fill {
  display: block;
  height: 100%;
  border-radius: var(--radius-full);
  transform-origin: left;
  animation: growX 500ms var(--ease-smooth) both;
  transition: width var(--duration-slow) var(--ease-smooth);
}
.rank-bars__row:hover .rank-bars__fill { filter: brightness(1.1); }
.rank-bars__value { min-width: 2ch; text-align: right; }

.meter { display: flex; flex-direction: column; gap: 6px; }
.meter__head { display: flex; justify-content: space-between; font-size: 12.5px; }
.meter__track { height: 8px; background: var(--surface-02); border-radius: var(--radius-full); overflow: hidden; }
.meter__fill {
  height: 100%;
  border-radius: var(--radius-full);
  transform-origin: left;
  animation: growX 700ms var(--ease-smooth) both;
  transition: width var(--duration-slow) var(--ease-smooth);
}

/* Shell nav: sliding active indicator */
.shell__nav-link { position: relative; }
.shell__nav-link::before {
  content: '';
  position: absolute;
  left: -12px;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: var(--accent);
  transform: scaleY(0);
  transition: transform var(--duration-normal) var(--ease-spring);
}
.shell__nav-link--active::before { transform: scaleY(1); }
@media (max-width: 860px) { .shell__nav-link::before { display: none; } }

/* Timeline animation */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Feedback primitives: dialog, toasts, refresh, segmented, skeletons */
.sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}
.modal-backdrop {
  position: fixed; inset: 0; z-index: 1000; display: grid; place-items: center;
  background: rgba(15, 23, 42, 0.35); animation: fadeIn var(--duration-fast) var(--ease-smooth);
}
.modal {
  width: min(440px, calc(100vw - 32px)); background: var(--surface-00);
  border-radius: var(--radius); box-shadow: var(--shadow-lg); padding: 20px;
  animation: popIn var(--duration-normal) var(--ease-spring);
}
.modal__title { margin: 0 0 8px; font-size: 16px; }
.modal__detail { color: var(--text-muted); font-size: 13px; }
.modal__actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes popIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: none; } }

.toast-viewport {
  position: fixed; top: calc(var(--shell-header) + 8px); right: 16px; z-index: 1100;
  display: flex; flex-direction: column; gap: 8px; pointer-events: none;
}
.toast {
  pointer-events: auto; display: flex; align-items: center; gap: 12px;
  min-width: 220px; max-width: 360px; padding: 10px 12px; border-radius: var(--radius-sm);
  background: var(--surface-00); box-shadow: var(--shadow-md); border-left: 3px solid var(--accent);
  font-size: 13px; animation: slideIn var(--duration-normal) var(--ease-smooth);
}
.toast--ok { border-left-color: var(--ok); }
.toast--error { border-left-color: var(--danger); }
.toast__close { margin-left: auto; border: 0; background: none; cursor: pointer; font-size: 16px; color: var(--text-faint); }

.refresh-indicator { display: inline-flex; align-items: center; gap: 8px; font-size: 12px; }
.refresh-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--ok); }
.refresh-dot--active { background: var(--accent); animation: pulse 1s ease-in-out infinite; }
@keyframes pulse { 50% { opacity: 0.3; } }

.query-state { position: relative; }
.query-state__refreshing {
  position: absolute; top: 4px; right: 8px; z-index: 2; font-size: 11.5px;
  color: var(--text-faint); background: var(--surface-00); padding: 0 6px; border-radius: var(--radius-full);
}
.query-state__stale { padding: 6px 12px; font-size: 12px; }

.segmented { display: inline-flex; padding: 2px; gap: 2px; background: var(--surface-02); border-radius: var(--radius-sm); }
.segmented__item {
  border: 0; background: transparent; padding: 4px 12px; border-radius: 5px; cursor: pointer;
  font: inherit; font-size: 12.5px; color: var(--text-muted);
  transition: background var(--duration-fast) var(--ease-smooth), color var(--duration-fast);
}
.segmented__item.is-active { background: var(--surface-00); color: var(--text); box-shadow: var(--shadow-sm); }
.segmented__item:focus-visible { outline: 2px solid var(--accent); outline-offset: 1px; }

.skeleton { padding: 12px; display: flex; flex-direction: column; gap: 10px; }
.skeleton--cards { flex-direction: row; flex-wrap: wrap; }
.skeleton-card, .skeleton-chart {
  border-radius: var(--radius-sm);
  background: linear-gradient(90deg, var(--surface-02), var(--surface-03), var(--surface-02));
  background-size: 200% 100%; animation: shimmer 1.4s linear infinite;
}
.skeleton-card { flex: 1 1 160px; height: 72px; }
.skeleton-chart { height: 180px; }
@keyframes shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }

.table-scroll { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
tr.row-enter > td { animation: rowEnter 1.4s var(--ease-smooth); }
@keyframes rowEnter { from { background: var(--accent-soft); } to { background: transparent; } }
.badge { transition: background var(--duration-normal), color var(--duration-normal); }
:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }

.user-menu { position: relative; }
.user-menu__panel {
  position: absolute; right: 0; top: calc(100% + 6px); z-index: 50; min-width: 180px;
  background: var(--surface-00); border-radius: var(--radius-sm); box-shadow: var(--shadow-lg);
  padding: 6px; display: flex; flex-direction: column; animation: popIn var(--duration-fast) var(--ease-smooth);
}
.user-menu__item {
  display: block; text-align: left; padding: 8px 10px; border: 0; background: none; font: inherit;
  color: var(--text); border-radius: 5px; cursor: pointer; text-decoration: none;
}
.user-menu__item:hover { background: var(--surface-02); }
.shell__nav-toggle { display: none; }
.shell__nav-backdrop { display: none; }
.skip-link {
  position: absolute; left: 8px; top: -48px; z-index: 1200; padding: 8px 12px;
  background: var(--surface-00); color: var(--text); border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md); text-decoration: none;
}
.skip-link:focus { top: 8px; }
.shell__main:focus { outline: none; }
@media (max-width: 860px) {
  .shell__nav--drawer.shell__nav--open { position: relative; z-index: 40; background: var(--surface-00); overflow-y: auto; }
  .shell__nav-backdrop {
    display: block; position: fixed; inset: var(--shell-header) 0 0 0; z-index: 30;
    border: 0; padding: 0; background: rgba(15, 23, 42, 0.25); cursor: pointer;
  }
}

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
}

@media print {
  .shell__header, .shell__nav, .toast-viewport, .no-print, .btn { display: none !important; }
  .shell, .shell__main { display: block !important; margin: 0 !important; }
  .panel { box-shadow: none !important; break-inside: avoid; }
  body { background: #fff; }
}

`;

export function installStyles(): void {
  const element = document.createElement('style');
  element.setAttribute('data-yq-styles', '');
  element.textContent = styles;
  document.head.appendChild(element);
}
